import { Mail, Heart, Instagram, Youtube } from "lucide-react";
import cswLogo from "@/assets/csw-logo-new.png";

export const Footer = () => {
  const currentYear = new Date().getFullYear();
  
  return (
    <footer className="relative border-t border-border/50 bg-background overflow-hidden">
      {/* Gradient Background Effects */}
      <div className="absolute inset-0 bg-gradient-to-b from-background via-muted/30 to-background" />
      <div className="absolute bottom-0 left-1/4 w-96 h-96 bg-primary/5 rounded-full blur-[100px]" />
      <div className="absolute bottom-0 right-1/4 w-96 h-96 bg-secondary/5 rounded-full blur-[100px]" />
      
      <div className="container relative z-10 mx-auto px-4 sm:px-6 lg:px-8 py-16">
        <div className="grid md:grid-cols-3 lg:grid-cols-6 gap-12 mb-12">
          {/* Brand Section */}
          <div className="space-y-6">
            <a href="/" className="flex items-center gap-3 hover:opacity-80 transition-opacity">
              <img src={cswLogo} alt="CSW" className="h-12 w-auto" />
              <span className="font-display text-3xl font-bold text-foreground">CSW</span>
            </a>
            <p className="text-muted-foreground leading-relaxed max-w-sm">
              An online workspace built to support focus and structure across school and work. With core productivity tools like task planning, Pomodoro timers, and a clean, distraction-free interface.
            </p>
            <div className="flex items-center gap-2 text-sm text-muted-foreground">
              <span>Made with</span>
              <Heart className="w-4 h-4 text-primary fill-primary" />
              <span>for productivity</span>
            </div>
          </div>
          
          {/* Tools & Features */}
          <div>
            <h3 className="font-display font-semibold text-foreground text-lg mb-4">Tools & Features</h3>
            <ul className="space-y-3">
              <li>
                <a href="/online-workspace/" className="text-muted-foreground hover:text-primary transition-colors inline-flex items-center gap-2 group">
                  <span className="relative">
                    Online Workspace
                    <span className="absolute bottom-0 left-0 w-0 h-0.5 bg-gradient-to-r from-primary to-secondary group-hover:w-full transition-all duration-300"></span>
                  </span>
                </a>
              </li>
              <li>
                <a href="/productivity-tool/" className="text-muted-foreground hover:text-primary transition-colors inline-flex items-center gap-2 group">
                  <span className="relative">
                    Productivity Tool
                    <span className="absolute bottom-0 left-0 w-0 h-0.5 bg-gradient-to-r from-primary to-secondary group-hover:w-full transition-all duration-300"></span>
                  </span>
                </a>
              </li>
              <li>
                <a href="/study-timer/" className="text-muted-foreground hover:text-primary transition-colors inline-flex items-center gap-2 group">
                  <span className="relative">
                    Study Timer
                    <span className="absolute bottom-0 left-0 w-0 h-0.5 bg-gradient-to-r from-primary to-secondary group-hover:w-full transition-all duration-300"></span>
                  </span>
                </a>
              </li>
              <li>
                <a href="https://pomotime.live/" target="_blank" rel="noopener noreferrer" className="text-muted-foreground hover:text-primary transition-colors inline-flex items-center gap-2 group">
                  <span className="relative">
                    Pomodoro Timer
                    <span className="absolute bottom-0 left-0 w-0 h-0.5 bg-gradient-to-r from-primary to-secondary group-hover:w-full transition-all duration-300"></span>
                  </span>
                </a>
              </li>
              <li>
                <a href="/task-management/" className="text-muted-foreground hover:text-primary transition-colors inline-flex items-center gap-2 group">
                  <span className="relative">
                    Task Management
                    <span className="absolute bottom-0 left-0 w-0 h-0.5 bg-gradient-to-r from-primary to-secondary group-hover:w-full transition-all duration-300"></span>
                  </span>
                </a>
              </li>
              <li>
                <a href="/reminders/" className="text-muted-foreground hover:text-primary transition-colors inline-flex items-center gap-2 group">
                  <span className="relative">
                    Reminders
                    <span className="absolute bottom-0 left-0 w-0 h-0.5 bg-gradient-to-r from-primary to-secondary group-hover:w-full transition-all duration-300"></span>
                  </span>
                </a>
              </li>
            </ul>
          </div>
          
          {/* Study Resources */}
          <div>
            <h3 className="font-display font-semibold text-foreground text-lg mb-4">Study Resources</h3>
            <ul className="space-y-3">
              <li>
                <a href="/quizzes/" className="text-muted-foreground hover:text-primary transition-colors inline-flex items-center gap-2 group">
                  <span className="relative">
                    Quizzes
                    <span className="absolute bottom-0 left-0 w-0 h-0.5 bg-gradient-to-r from-primary to-secondary group-hover:w-full transition-all duration-300"></span>
                  </span>
                </a>
              </li>
              <li>
                <a href="/flash-cards/" className="text-muted-foreground hover:text-primary transition-colors inline-flex items-center gap-2 group">
                  <span className="relative">
                    Flash Cards
                    <span className="absolute bottom-0 left-0 w-0 h-0.5 bg-gradient-to-r from-primary to-secondary group-hover:w-full transition-all duration-300"></span>
                  </span>
                </a>
              </li>
              <li>
                <a href="/study-stream/" className="text-muted-foreground hover:text-primary transition-colors inline-flex items-center gap-2 group">
                  <span className="relative">
                    Study Stream
                    <span className="absolute bottom-0 left-0 w-0 h-0.5 bg-gradient-to-r from-primary to-secondary group-hover:w-full transition-all duration-300"></span>
                  </span>
                </a>
              </li>
              <li>
                <a href="/studyverse/" className="text-muted-foreground hover:text-primary transition-colors inline-flex items-center gap-2 group">
                  <span className="relative">
                    StudyVerse Alternative
                    <span className="absolute bottom-0 left-0 w-0 h-0.5 bg-gradient-to-r from-primary to-secondary group-hover:w-full transition-all duration-300"></span>
                  </span>
                </a>
              </li>
              <li>
                <a href="https://app.csw.live/signup" className="text-muted-foreground hover:text-primary transition-colors inline-flex items-center gap-2 group">
                  <span className="relative">
                    Sign Up Free
                    <span className="absolute bottom-0 left-0 w-0 h-0.5 bg-gradient-to-r from-primary to-secondary group-hover:w-full transition-all duration-300"></span>
                  </span>
                </a>
              </li>
            </ul>
          </div>
          
          {/* Study Modes */}
          <div>
            <h3 className="font-display font-semibold text-foreground text-lg mb-4">Study Modes</h3>
            <ul className="space-y-3">
              <li>
                <a href="/study-room/" className="text-muted-foreground hover:text-primary transition-colors inline-flex items-center gap-2 group">
                  <span className="relative">
                    Study Room
                    <span className="absolute bottom-0 left-0 w-0 h-0.5 bg-gradient-to-r from-primary to-secondary group-hover:w-full transition-all duration-300"></span>
                  </span>
                </a>
              </li>
              <li>
                <a href="/study-with-me/" className="text-muted-foreground hover:text-primary transition-colors inline-flex items-center gap-2 group">
                  <span className="relative">
                    Study with Me
                    <span className="absolute bottom-0 left-0 w-0 h-0.5 bg-gradient-to-r from-primary to-secondary group-hover:w-full transition-all duration-300"></span>
                  </span>
                </a>
              </li>
              <li>
                <a href="/study-with-friends/" className="text-muted-foreground hover:text-primary transition-colors inline-flex items-center gap-2 group">
                  <span className="relative">
                    Study with Friends
                    <span className="absolute bottom-0 left-0 w-0 h-0.5 bg-gradient-to-r from-primary to-secondary group-hover:w-full transition-all duration-300"></span>
                  </span>
                </a>
              </li>
              <li>
                <a href="/study-with-strangers/" className="text-muted-foreground hover:text-primary transition-colors inline-flex items-center gap-2 group">
                  <span className="relative">
                    Study with Strangers
                    <span className="absolute bottom-0 left-0 w-0 h-0.5 bg-gradient-to-r from-primary to-secondary group-hover:w-full transition-all duration-300"></span>
                  </span>
                </a>
              </li>
              <li>
                <a href="/group-study/" className="text-muted-foreground hover:text-primary transition-colors inline-flex items-center gap-2 group">
                  <span className="relative">
                    Group Study
                    <span className="absolute bottom-0 left-0 w-0 h-0.5 bg-gradient-to-r from-primary to-secondary group-hover:w-full transition-all duration-300"></span>
                  </span>
                </a>
              </li>
              <li>
                <a href="/zoom-study-room/" className="text-muted-foreground hover:text-primary transition-colors inline-flex items-center gap-2 group">
                  <span className="relative">
                    Zoom Study Room
                    <span className="absolute bottom-0 left-0 w-0 h-0.5 bg-gradient-to-r from-primary to-secondary group-hover:w-full transition-all duration-300"></span>
                  </span>
                </a>
              </li>
              <li>
                <a href="/online-workspace/" className="text-muted-foreground hover:text-primary transition-colors inline-flex items-center gap-2 group">
                  <span className="relative">
                    Study Together Online
                    <span className="absolute bottom-0 left-0 w-0 h-0.5 bg-gradient-to-r from-primary to-secondary group-hover:w-full transition-all duration-300"></span>
                  </span>
                </a>
              </li>
            </ul>
          </div>
          
          {/* Study with Kpop */}
          <div className="col-span-1 md:col-span-2">
            <h3 className="font-display font-semibold text-foreground text-lg mb-4">Study with Kpop</h3>
            <ul className="grid grid-cols-2 gap-x-6 gap-y-3">
              {[
                { name: "Study with Kpop", link: "/study-with-kpop/" },
                { name: "Study with SKZ", link: "/study-with-skz/" },
                { name: "Study with ITZY", link: "/study-with-itzy/" },
                { name: "Study with BLACKPINK", link: "/study-with-blackpink/" },
                { name: "Study with aespa", link: "/study-with-aespa/" },
                { name: "Study with TWICE", link: "/study-with-twice/" },
                { name: "Study with ATEEZ", link: "/study-with-ateez/" },
                { name: "Study with BTS", link: "/study-with-bts/" },
                { name: "Study with ENHYPEN", link: "/study-with-enhypen/" },
                { name: "Study with NCT", link: "/study-with-nct/" },
                { name: "Study with TXT", link: "/study-with-txt/" },
                { name: "Study with SEVENTEEN", link: "/study-with-seventeen/" },
                { name: "Study with EXO", link: "/study-with-exo/" },
                { name: "Study with MONSTA X", link: "/study-with-monsta-x/" },
                { name: "Study with THE BOYZ", link: "/study-with-the-boyz/" },
                { name: "Study with TREASURE", link: "/study-with-treasure/" },
                { name: "Study with GOT7", link: "/study-with-got7/" },
                { name: "Study with SF9", link: "/study-with-sf9/" },
                { name: "Study with LOONA", link: "/study-with-loona/" },
                { name: "Study with Red Velvet", link: "/study-with-red-velvet/" },
                { name: "Study with MAMAMOO", link: "/study-with-mamamoo/" },
              ].map((item) => (
                <li key={item.link}>
                  <a href={item.link} className="text-muted-foreground hover:text-primary transition-colors inline-flex items-center gap-2 group">
                    <span className="relative">
                      {item.name}
                      <span className="absolute bottom-0 left-0 w-0 h-0.5 bg-gradient-to-r from-primary to-secondary group-hover:w-full transition-all duration-300"></span>
                    </span>
                  </a>
                </li>
              ))}
            </ul>
          </div>
          
          {/* Global Community */}
          <div>
            <h3 className="font-display font-semibold text-foreground text-lg mb-4">Global Community</h3>
            <ul className="space-y-3 text-sm">
              <li>
                <a href="/jp/" className="text-muted-foreground hover:text-primary transition-colors inline-flex items-center gap-2 group">
                  <span className="relative">
                    オンライン自習室
                    <span className="absolute bottom-0 left-0 w-0 h-0.5 bg-gradient-to-r from-primary to-secondary group-hover:w-full transition-all duration-300"></span>
                  </span>
                </a>
              </li>
              <li>
                <a href="/vn/" className="text-muted-foreground hover:text-primary transition-colors inline-flex items-center gap-2 group">
                  <span className="relative">
                    Phòng học ảo
                    <span className="absolute bottom-0 left-0 w-0 h-0.5 bg-gradient-to-r from-primary to-secondary group-hover:w-full transition-all duration-300"></span>
                  </span>
                </a>
              </li>
              <li>
                <a href="/cn/" className="text-muted-foreground hover:text-primary transition-colors inline-flex items-center gap-2 group">
                  <span className="relative">
                    线上自修室
                    <span className="absolute bottom-0 left-0 w-0 h-0.5 bg-gradient-to-r from-primary to-secondary group-hover:w-full transition-all duration-300"></span>
                  </span>
                </a>
              </li>
              <li>
                <a href="/tw/" className="text-muted-foreground hover:text-primary transition-colors inline-flex items-center gap-2 group">
                  <span className="relative">
                    自修室
                    <span className="absolute bottom-0 left-0 w-0 h-0.5 bg-gradient-to-r from-primary to-secondary group-hover:w-full transition-all duration-300"></span>
                  </span>
                </a>
              </li>
              <li>
                <a href="/kr/" className="text-muted-foreground hover:text-primary transition-colors inline-flex items-center gap-2 group">
                  <span className="relative">
                    온라인 자습실
                    <span className="absolute bottom-0 left-0 w-0 h-0.5 bg-gradient-to-r from-primary to-secondary group-hover:w-full transition-all duration-300"></span>
                  </span>
                </a>
              </li>
            </ul>
          </div>
          
          {/* Connect */}
          <div>
            <h3 className="font-display font-semibold text-foreground text-lg mb-4">Connect</h3>
            <ul className="space-y-3">
              <li>
                <a href="mailto:info@csw.live" className="text-muted-foreground hover:text-primary transition-colors inline-flex items-center gap-2 group">
                  <Mail className="w-4 h-4" />
                  <span className="relative">
                    Contact Us
                    <span className="absolute bottom-0 left-0 w-0 h-0.5 bg-gradient-to-r from-primary to-secondary group-hover:w-full transition-all duration-300"></span>
                  </span>
                </a>
              </li>
              <li>
                <a href="https://www.instagram.com/csw.live/" target="_blank" rel="noopener noreferrer" className="text-muted-foreground hover:text-primary transition-colors inline-flex items-center gap-2 group">
                  <Instagram className="w-4 h-4" />
                  <span className="relative">
                    Instagram
                    <span className="absolute bottom-0 left-0 w-0 h-0.5 bg-gradient-to-r from-primary to-secondary group-hover:w-full transition-all duration-300"></span>
                  </span>
                </a>
              </li>
              <li>
                <a href="https://www.youtube.com/@CSW.live.workspace" target="_blank" rel="noopener noreferrer" className="text-muted-foreground hover:text-primary transition-colors inline-flex items-center gap-2 group">
                  <Youtube className="w-4 h-4" />
                  <span className="relative">
                    YouTube
                    <span className="absolute bottom-0 left-0 w-0 h-0.5 bg-gradient-to-r from-primary to-secondary group-hover:w-full transition-all duration-300"></span>
                  </span>
                </a>
              </li>
            </ul>
          </div>
        </div>
        
        {/* Bottom Bar */}
        <div className="pt-8 border-t border-border/50">
          <div className="flex flex-col sm:flex-row justify-between items-center gap-4">
            <p className="text-muted-foreground text-sm">
              © {currentYear} CSW. All rights reserved.
            </p>
            <div className="flex items-center gap-6 text-sm flex-wrap justify-center sm:justify-end">
              <a href="/privacy-policy/" className="text-muted-foreground hover:text-primary transition-colors">Privacy Policy</a>
              <a href="/terms-of-use/" className="text-muted-foreground hover:text-primary transition-colors">Terms of Use</a>
              <a href="/registration-policy/" className="text-muted-foreground hover:text-primary transition-colors">Registration Policy</a>
              <a href="/content-monitoring-policy/" className="text-muted-foreground hover:text-primary transition-colors">Content Monitoring</a>
              <a href="/report-policy/" className="text-muted-foreground hover:text-primary transition-colors">Report Policy</a>
            </div>
          </div>
        </div>
      </div>
    </footer>
  );
};
